import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Pagination,
  Skeleton,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography
} from '@mui/material';
import {
  MdAdd,
  MdArrowBack,
  MdCheckCircle,
  MdContentCopy,
  MdDeleteForever,
  MdEdit,
  MdEventBusy,
  MdLocalOffer,
  MdPauseCircle,
  MdPeople,
  MdSearch,
  MdTune,
  MdVisibility,
  MdWarningAmber
} from 'react-icons/md';
import { postData, getData, deleteData, putData } from '../../Webservice/Webservice';

const FONT = 'Mulish, sans-serif';

const authToken = () => {
  const raw = sessionStorage.getItem('token');
  return raw ? `Bearer ${raw}` : '';
};

const STATUS = {
  active: { label: 'Active', bg: '#dcfce7', fg: '#166534', accent: '#10b981', gradient: 'linear-gradient(135deg,#10b981,#059669)' },
  inactive: { label: 'Inactive', bg: '#f1f5f9', fg: '#475569', accent: '#64748b', gradient: 'linear-gradient(135deg,#64748b,#475569)' },
  expired: { label: 'Expired', bg: '#fee2e2', fg: '#991b1b', accent: '#ef4444', gradient: 'linear-gradient(135deg,#ef4444,#dc2626)' }
};

const AUDIENCE = { all: 'Everyone', b2b: 'B2B only', b2c: 'B2C only' };

const EMPTY_FORM = {
  code: '',
  type: 'percentage',
  discount: '',
  maxDiscount: '',
  minOrderAmount: '',
  usageLimit: '',
  perUserLimit: '',
  validFrom: '',
  validTill: '',
  isFirstTimeUserOnly: false,
  applicableTo: 'all',
  applicableCardTypes: [],
  allowedCardBins: [],
  status: 'active',
  role_id: '',
  assignTo: ''
};
const EMPTY_FILTERS = { code: '', type: '', status: '', validFrom: '', validTill: '' };

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const fmtDateTime = (d) => {
  if (!d) return '-';
  const date = new Date(d);
  if (isNaN(date.getTime())) return String(d);
  return `${date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}, ${date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}`;
};
const fmtDay = (d) => {
  if (!d) return '-';
  const date = new Date(d);
  return isNaN(date.getTime()) ? String(d) : date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};
const discountText = (c) => (c.type === 'percentage' ? `${c.discount}%` : inr(c.discount));
const toList = (v) => (Array.isArray(v) ? v : typeof v === 'string' ? v.split(',').map((x) => x.trim()).filter(Boolean) : []);
const personName = (u) => (u ? u.name || `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || '' : '');

// What the customer effectively sees: an "active" coupon past its end date is expired.
const effectiveStatus = (c) => {
  if (c.status === 'active' && c.validTill) {
    const till = new Date(c.validTill).getTime();
    if (!isNaN(till) && till < Date.now()) return 'expired';
  }
  return c.status;
};

const transformCoupon = (coupon) => {
  const assigned = coupon.assignTo && typeof coupon.assignTo === 'object' ? coupon.assignTo : null;
  return {
    id: coupon._id,
    code: coupon.code,
    type: coupon.type === 'fixed' ? 'flat' : coupon.type,
    discount: coupon.discount,
    maxDiscount: coupon.maxDiscount || null,
    minOrderAmount: coupon.minOrderAmount,
    usageLimit: coupon.usageLimit,
    perUserLimit: coupon.perUserLimit,
    validFrom: coupon.validFrom,
    validTill: coupon.validTill,
    isFirstTimeUserOnly: coupon.isFirstTimeUserOnly,
    applicableTo: Array.isArray(coupon.applicableTo) ? coupon.applicableTo[0] || 'all' : coupon.applicableTo || 'all',
    applicableCardTypes: coupon.applicableCardTypes || [],
    allowedCardBins: coupon.allowedCardBins || [],
    status: coupon.status,
    // total redemptions (a customer can use it more than once)
    used: (coupon.usedBy || []).reduce((a, u) => a + (u.usageCount || 1), 0),
    assignTo: assigned ? assigned._id || assigned.id : coupon.assignTo || null,
    assignToName: assigned ? personName(assigned) : null,
    role_id: assigned?.role_id?._id || assigned?.role_id?.id || null
  };
};

const toForm = (c) => ({
  code: c.code || '',
  type: c.type || 'percentage',
  discount: c.discount ?? '',
  maxDiscount: c.maxDiscount || '',
  minOrderAmount: c.minOrderAmount ?? '',
  usageLimit: c.usageLimit || '',
  perUserLimit: c.perUserLimit || '',
  validFrom: c.validFrom || '',
  validTill: c.validTill || '',
  isFirstTimeUserOnly: !!c.isFirstTimeUserOnly,
  applicableTo: c.applicableTo || 'all',
  applicableCardTypes: c.applicableCardTypes || [],
  allowedCardBins: c.allowedCardBins || [],
  status: c.status || 'active',
  role_id: c.role_id || '',
  assignTo: c.assignTo || ''
});

// ---------- small presentational pieces (module level so inputs keep focus) ----------
const Pill = ({ status }) => {
  const s = STATUS[status] || STATUS.inactive;
  return <Chip size="small" label={s.label} sx={{ fontFamily: FONT, fontWeight: 700, background: s.bg, color: s.fg, height: 24 }} />;
};

const FormCard = ({ title, subtitle, accent, icon: Icon, children }) => (
  <Paper elevation={0} sx={{ p: 3, borderRadius: '14px', border: '1px solid #e2e8f0', borderTop: `4px solid ${accent}`, boxShadow: '0 4px 20px rgba(15,23,42,.04)' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
      <Box sx={{ background: `${accent}1a`, color: accent, borderRadius: '10px', p: 1, display: 'flex' }}><Icon size={18} /></Box>
      <Box>
        <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 14.5, color: '#0f172a', lineHeight: 1.2 }}>{title}</Typography>
        <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#64748b' }}>{subtitle}</Typography>
      </Box>
    </Box>
    {children}
  </Paper>
);

// Live preview of the coupon as the customer will see it.
const CouponPreview = ({ form }) => {
  const hasDiscount = form.discount !== '' && !isNaN(Number(form.discount));
  const shown = hasDiscount ? discountText({ type: form.type, discount: form.discount }) : '--';
  const st = STATUS[effectiveStatus({ status: form.status, validTill: form.validTill })] || STATUS.inactive;
  return (
    <Paper elevation={0} sx={{ borderRadius: '16px', overflow: 'hidden', border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(15,23,42,.06)' }}>
      <Box sx={{ background: 'linear-gradient(135deg,#0f172a,#334155)', color: '#fff', p: 3, textAlign: 'center' }}>
        <Typography sx={{ fontFamily: FONT, fontSize: 11, fontWeight: 700, opacity: 0.8, letterSpacing: '0.08em' }}>PREVIEW</Typography>
        <Typography sx={{ fontFamily: FONT, fontSize: 39.5, fontWeight: 800, lineHeight: 1.1, my: 0.5 }}>{shown}<Typography component="span" sx={{ fontSize: 16, fontWeight: 700, ml: 0.75 }}>OFF</Typography></Typography>
        {form.type === 'percentage' && form.maxDiscount !== '' && <Typography sx={{ fontFamily: FONT, fontSize: 11.5, opacity: 0.85 }}>up to {inr(form.maxDiscount)}</Typography>}
      </Box>
      <Box sx={{ p: 2.5 }}>
        <Box sx={{ border: '2px dashed #cbd5e1', borderRadius: '10px', py: 1, textAlign: 'center', fontFamily: 'monospace', fontWeight: 800, letterSpacing: '3px', fontSize: 16, color: '#0f172a', mb: 2 }}>
          {(form.code || 'COUPONCODE').toUpperCase()}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {[
            ['Min. order', form.minOrderAmount !== '' ? inr(form.minOrderAmount) : '-'],
            ['Valid from', fmtDay(form.validFrom)],
            ['Valid till', fmtDay(form.validTill)],
            ['For', AUDIENCE[form.applicableTo] || '-'],
            ['Limit', form.usageLimit ? `${form.usageLimit} uses` : 'Unlimited']
          ].map(([k, v]) => (
            <Box key={k} sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#64748b' }}>{k}</Typography>
              <Typography sx={{ fontFamily: FONT, fontSize: 11.5, fontWeight: 700, color: '#0f172a' }}>{v}</Typography>
            </Box>
          ))}
        </Box>
        {form.isFirstTimeUserOnly && <Chip size="small" label="First-time customers only" sx={{ mt: 1.5, fontFamily: FONT, fontWeight: 700, background: '#ede9fe', color: '#5b21b6' }} />}
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
          <Chip size="small" label={st.label} sx={{ fontFamily: FONT, fontWeight: 700, background: st.bg, color: st.fg }} />
        </Box>
      </Box>
    </Paper>
  );
};

const CouponsManagement = () => {
  const navigate = useNavigate();
  const { couponId } = useParams();

  const [coupons, setCoupons] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0, expired: 0 });
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [codeInput, setCodeInput] = useState('');
  const [meta, setMeta] = useState({ page: 1, totalPages: 1, total: 0, limit: 12 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const [showForm, setShowForm] = useState(false);
  const [currentCoupon, setCurrentCoupon] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formMode, setFormMode] = useState('edit');
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // ---------- data ----------
  const fetchCoupons = useCallback(async (page, limit) => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, limit, ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== '')) };
      const res = await getData('coupon/list', params, authToken(), false);
      if (res && Array.isArray(res.coupons)) {
        setCoupons(res.coupons.map(transformCoupon));
        if (res.stats) setStats(res.stats);
        setMeta((prev) => ({ ...prev, ...(res.meta || {}), limit }));
      } else {
        setCoupons([]);
        setError('Failed to fetch coupons');
      }
    } catch (err) {
      setCoupons([]);
      setError('Failed to fetch coupons. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchCoupons(meta.page, meta.limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta.page, meta.limit, fetchCoupons]);

  // wait for the user to stop typing before searching by code
  useEffect(() => {
    const t = setTimeout(() => {
      setFilters((prev) => (prev.code === codeInput.trim() ? prev : { ...prev, code: codeInput.trim() }));
      setMeta((prev) => ({ ...prev, page: 1 }));
    }, 400);
    return () => clearTimeout(t);
  }, [codeInput]);

  const setFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setMeta((prev) => ({ ...prev, page: 1 }));
  };
  const hasFilters = Object.values(filters).some(Boolean);
  const clearFilters = () => {
    setCodeInput('');
    setFilters(EMPTY_FILTERS);
    setMeta((prev) => ({ ...prev, page: 1 }));
  };

  // ---------- open / close form ----------
  const openCoupon = useCallback((coupon) => {
    setIsEditing(true);
    setCurrentCoupon(coupon);
    setFormMode('view');
    setFormData(toForm(coupon));
    setErrors({});
    setShowForm(true);
  }, []);

  const handleOpenEditForm = (coupon, mode = 'view') => {
    openCoupon(coupon);
    setFormMode(mode);
    navigate(`/couponsmanagement/${coupon.id}`);
  };

  // Deep link: /couponsmanagement/:couponId opens that coupon even if it is not on the current page.
  useEffect(() => {
    if (!couponId) {
      setShowForm(false);
      return;
    }
    if (currentCoupon?.id === couponId && showForm) return;
    let cancelled = false;
    (async () => {
      const inList = coupons.find((c) => c.id === couponId);
      if (inList) return openCoupon(inList);
      try {
        const res = await getData(`coupon/${couponId}`, undefined, authToken(), false);
        if (!cancelled && res && res._id) openCoupon(transformCoupon(res));
      } catch (err) {
        if (!cancelled) setError('Could not open that coupon');
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [couponId]);

  const handleOpenAddForm = () => {
    setIsEditing(false);
    setCurrentCoupon(null);
    setFormMode('edit');
    setFormData(EMPTY_FORM);
    setErrors({});
    setShowForm(true);
    if (couponId) navigate('/couponsmanagement');
  };

  const handleCloseForm = () => {
    setShowForm(false);
    navigate('/couponsmanagement');
  };

  // roles / users for the "assign to" fields
  useEffect(() => {
    if (!showForm) return;
    getData('user/role', undefined, authToken(), false).then((res) => {
      if (Array.isArray(res)) setRoles(res);
      else if (res && Array.isArray(res.roles)) setRoles(res.roles);
    });
    getData('user/list', undefined, authToken(), false).then((res) => {
      if (Array.isArray(res)) setUsers(res);
      else if (res && Array.isArray(res.users)) setUsers(res.users);
    });
  }, [showForm]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value, ...(name === 'role_id' ? { assignTo: '' } : {}) }));
  };

  const filteredUsers = useMemo(() => {
    if (!formData.role_id) return users;
    return users.filter((u) => {
      const rid = typeof u.role_id === 'object' && u.role_id !== null ? u.role_id._id || u.role_id.id : u.role_id;
      return rid === formData.role_id;
    });
  }, [users, formData.role_id]);

  // ---------- validation ----------
  const validateForm = () => {
    const e = {};
    if (!formData.code || !formData.code.toString().trim()) e.code = 'Coupon code is required';

    const discountVal = formData.discount === '' || formData.discount === null ? NaN : Number(formData.discount);
    if (isNaN(discountVal)) {
      e.discount = formData.type === 'percentage' ? 'Discount percentage is required' : 'Discount amount is required';
    } else if (formData.type === 'percentage' && (discountVal <= 0 || discountVal > 100)) {
      e.discount = 'Enter a percentage between 1 and 100';
    } else if (discountVal <= 0) {
      e.discount = 'Discount must be greater than 0';
    }
    if (formData.type === 'percentage' && formData.maxDiscount !== '' && formData.maxDiscount !== null) {
      const md = Number(formData.maxDiscount);
      if (isNaN(md) || md < 0) e.maxDiscount = 'Max discount must be a non-negative number';
    }

    const minOrder = Number(formData.minOrderAmount);
    if (formData.minOrderAmount === '' || formData.minOrderAmount === null || isNaN(minOrder)) e.minOrderAmount = 'Minimum order amount is required';
    else if (minOrder < 0) e.minOrderAmount = 'Minimum order amount cannot be negative';

    const usageLimit = Number(formData.usageLimit);
    const perUserLimit = Number(formData.perUserLimit);
    if (formData.usageLimit !== '' && formData.usageLimit !== null && (isNaN(usageLimit) || !Number.isInteger(usageLimit) || usageLimit <= 0)) {
      e.usageLimit = 'Usage limit must be a positive whole number';
    }
    if (formData.perUserLimit !== '' && formData.perUserLimit !== null) {
      if (isNaN(perUserLimit) || !Number.isInteger(perUserLimit) || perUserLimit <= 0) e.perUserLimit = 'Per-user limit must be a positive whole number';
      else if (formData.usageLimit !== '' && !isNaN(usageLimit) && perUserLimit > usageLimit) e.perUserLimit = 'Per-user limit cannot exceed the overall usage limit';
    }

    if (!formData.validFrom) e.validFrom = 'Valid from date is required';
    if (!formData.validTill) e.validTill = 'Valid till date is required';
    if (formData.validFrom && formData.validTill) {
      const from = new Date(formData.validFrom).getTime();
      const till = new Date(formData.validTill).getTime();
      if (isNaN(from)) e.validFrom = 'Invalid start date';
      if (isNaN(till)) e.validTill = 'Invalid end date';
      if (!isNaN(from) && !isNaN(till) && from >= till) e.validTill = 'Valid till must be after valid from';
    }

    if (!formData.applicableTo) e.applicableTo = 'Please select who can use this coupon';

    const bins = toList(formData.allowedCardBins);
    const badBins = bins.filter((b) => !/^\d{4,6}$/.test(b));
    if (badBins.length) e.allowedCardBins = `Invalid BIN(s): ${badBins.join(', ')} — use 4 to 6 digits`;

    if (!['active', 'inactive', 'expired'].includes(formData.status)) e.status = 'Invalid status selected';

    if (formData.role_id && formData.assignTo) {
      const found = users.find((u) => (u._id || u.id) === formData.assignTo);
      if (found) {
        const rid = typeof found.role_id === 'object' && found.role_id !== null ? found.role_id._id || found.role_id.id : found.role_id;
        if (rid !== formData.role_id) e.assignTo = 'Selected user does not belong to the selected role';
      }
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // clear a field's error as soon as the user edits it
  const clearError = (name) => setErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev));

  const handleSubmit = async (ev) => {
    if (ev && typeof ev.preventDefault === 'function') ev.preventDefault();
    if (!validateForm() || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const payload = {
        code: formData.code.trim(),
        type: formData.type,
        discount: Number(formData.discount),
        maxDiscount: formData.type === 'percentage' && formData.maxDiscount !== '' && formData.maxDiscount !== null ? Number(formData.maxDiscount) : null,
        minOrderAmount: Number(formData.minOrderAmount),
        usageLimit: formData.usageLimit ? Number(formData.usageLimit) : null,
        perUserLimit: formData.perUserLimit ? Number(formData.perUserLimit) : null,
        validFrom: formData.validFrom,
        validTill: formData.validTill,
        isFirstTimeUserOnly: formData.isFirstTimeUserOnly,
        applicableTo: formData.applicableTo,
        applicableCardTypes: toList(formData.applicableCardTypes),
        allowedCardBins: toList(formData.allowedCardBins),
        status: formData.status,
        role_id: formData.role_id || null,
        assignTo: formData.assignTo || null
      };
      const response = isEditing
        ? await putData(`coupon/update/${currentCoupon.id}`, payload, authToken())
        : await postData('coupon/add', payload, authToken());
      if (response) {
        await fetchCoupons(meta.page, meta.limit);
        handleCloseForm();
      }
    } catch (err) {
      setError('Failed to save the coupon. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const response = await deleteData(`coupon/delete/${deleteTarget.id}`, authToken());
      if (response) {
        const lastOnPage = coupons.length === 1 && meta.page > 1;
        if (lastOnPage) setMeta((prev) => ({ ...prev, page: prev.page - 1 }));
        else await fetchCoupons(meta.page, meta.limit);
      } else {
        setError('Failed to delete coupon');
      }
    } catch (err) {
      setError('Failed to delete coupon. Please try again.');
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const copyCode = (c) => {
    navigator.clipboard?.writeText(c.code);
    setCopiedId(c.id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  // ---------- form field helpers ----------
  const viewField = (label, value) => (
    <Box>
      <Typography sx={{ fontFamily: FONT, fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</Typography>
      <Typography sx={{ fontFamily: FONT, fontSize: 13.5, fontWeight: 600, color: '#0f172a', wordBreak: 'break-word' }}>{value === '' || value === null || value === undefined ? '-' : value}</Typography>
    </Box>
  );

  const field = (name, label, opts = {}) => {
    const { type, select, options, helper, adornment, view, required = false, min, shrink } = opts;
    if (formMode === 'view') return viewField(label, view ?? formData[name]);
    return (
      <TextField
        fullWidth
        size="small"
        select={!!select}
        label={label}
        name={name}
        type={type}
        value={formData[name]}
        onChange={(e) => { handleInputChange(e); clearError(name); }}
        required={required}
        error={!!errors[name]}
        helperText={errors[name] || helper}
        InputLabelProps={shrink ? { shrink: true } : undefined}
        InputProps={adornment ? { endAdornment: <InputAdornment position="end">{adornment}</InputAdornment> } : undefined}
        inputProps={min !== undefined ? { min } : type === 'number' ? { min: 0 } : undefined}
      >
        {select && options.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
      </TextField>
    );
  };

  const listField = (name, label, placeholder, helper) => {
    const text = toList(formData[name]).join(', ');
    if (formMode === 'view') return viewField(label, text);
    return (
      <TextField
        fullWidth
        size="small"
        label={label}
        placeholder={placeholder}
        defaultValue={text}
        key={`${name}-${currentCoupon?.id || 'new'}`}
        onChange={(e) => { setFormData((prev) => ({ ...prev, [name]: toList(e.target.value) })); clearError(name); }}
        error={!!errors[name]}
        helperText={errors[name] || helper}
      />
    );
  };

  // ---------- render: form ----------
  const renderForm = () => {
    const title = !isEditing ? 'Create Coupon' : formMode === 'edit' ? 'Edit Coupon' : 'View Coupon';
    const roleName = roles.find((r) => (r._id || r.id) === formData.role_id)?.name;
    const assignedUser = users.find((u) => (u._id || u.id) === formData.assignTo);
    return (
      <>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <IconButton onClick={handleCloseForm} aria-label="back" sx={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px' }}>
              <MdArrowBack size={20} />
            </IconButton>
            <Box>
              <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 23.5, color: '#0f172a', lineHeight: 1.2 }}>{title}</Typography>
              <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#64748b' }}>
                {isEditing ? formData.code : 'Set the discount, rules and validity, then publish.'}
              </Typography>
            </Box>
            {isEditing && <Pill status={effectiveStatus({ status: formData.status, validTill: formData.validTill })} />}
          </Box>
          {isEditing && (
            <Button
              onClick={() => setFormMode((m) => (m === 'view' ? 'edit' : 'view'))}
              startIcon={formMode === 'view' ? <MdEdit /> : <MdVisibility />}
              sx={{ fontFamily: FONT, textTransform: 'none', fontWeight: 700, borderRadius: '10px', px: 2.5, color: '#0f172a', background: formMode === 'view' ? 'rgb(255 209 2)' : '#e2e8f0', '&:hover': { background: formMode === 'view' ? 'rgb(255 190 0)' : '#cbd5e1' } }}
            >
              {formMode === 'view' ? 'Edit coupon' : 'Switch to view'}
            </Button>
          )}
        </Box>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={2.5}>
            <Grid item xs={12} lg={8}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <FormCard title="Discount" subtitle="What the customer gets" accent="#6366f1" icon={MdLocalOffer}>
                  <Grid container spacing={2.5}>
                    <Grid item xs={12} md={6}>
                      {formMode === 'view' ? viewField('Coupon code', formData.code) : (
                        <TextField
                          fullWidth size="small" label="Coupon code" name="code" required
                          value={formData.code}
                          onChange={(e) => { setFormData((p) => ({ ...p, code: e.target.value.toUpperCase().replace(/\s/g, '') })); clearError('code'); }}
                          error={!!errors.code} helperText={errors.code || 'Customers type this at checkout'}
                          inputProps={{ style: { fontFamily: 'monospace', fontWeight: 700, letterSpacing: 2 } }}
                        />
                      )}
                    </Grid>
                    <Grid item xs={12} md={6}>
                      {formMode === 'view' ? viewField('Discount type', formData.type === 'percentage' ? 'Percentage' : 'Flat amount') : (
                        <Box>
                          <Typography sx={{ fontFamily: FONT, fontSize: 11, fontWeight: 700, color: '#64748b', mb: 0.5 }}>Discount type</Typography>
                          <ToggleButtonGroup
                            exclusive fullWidth size="small" value={formData.type}
                            onChange={(_, v) => { if (v) setFormData((p) => ({ ...p, type: v, discount: '', maxDiscount: '' })); }}
                          >
                            <ToggleButton value="percentage" sx={{ fontFamily: FONT, textTransform: 'none', fontWeight: 700 }}>Percentage (%)</ToggleButton>
                            <ToggleButton value="flat" sx={{ fontFamily: FONT, textTransform: 'none', fontWeight: 700 }}>Flat amount (₹)</ToggleButton>
                          </ToggleButtonGroup>
                        </Box>
                      )}
                    </Grid>
                    <Grid item xs={12} md={6}>
                      {field('discount', formData.type === 'percentage' ? 'Discount percentage' : 'Discount amount', {
                        type: 'number', required: true, adornment: formData.type === 'percentage' ? '%' : '₹',
                        view: discountText({ type: formData.type, discount: formData.discount })
                      })}
                    </Grid>
                    {formData.type === 'percentage' && (
                      <Grid item xs={12} md={6}>
                        {field('maxDiscount', 'Maximum discount (₹)', { type: 'number', helper: 'Optional – caps the discount amount', view: formData.maxDiscount ? inr(formData.maxDiscount) : 'No cap' })}
                      </Grid>
                    )}
                  </Grid>
                </FormCard>

                <FormCard title="Usage rules" subtitle="When and how often it can be used" accent="#f59e0b" icon={MdTune}>
                  <Grid container spacing={2.5}>
                    <Grid item xs={12} md={4}>
                      {field('minOrderAmount', 'Minimum order (₹)', { type: 'number', required: true, view: formData.minOrderAmount !== '' ? inr(formData.minOrderAmount) : '-' })}
                    </Grid>
                    <Grid item xs={12} md={4}>
                      {field('usageLimit', 'Total usage limit', { type: 'number', helper: 'Empty = unlimited', view: formData.usageLimit || 'Unlimited' })}
                    </Grid>
                    <Grid item xs={12} md={4}>
                      {field('perUserLimit', 'Limit per customer', { type: 'number', helper: 'Empty = default (1)', view: formData.perUserLimit || '1 (default)' })}
                    </Grid>
                    <Grid item xs={12}>
                      {formMode === 'view' ? viewField('First-time customers only', formData.isFirstTimeUserOnly ? 'Yes' : 'No') : (
                        <FormControlLabel
                          control={
                            <Switch
                              checked={formData.isFirstTimeUserOnly}
                              onChange={(e) => setFormData((p) => ({ ...p, isFirstTimeUserOnly: e.target.checked }))}
                              sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: 'rgb(255, 209, 2)' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: 'rgba(245, 216, 87, 1)' } }}
                            />
                          }
                          label={<Typography sx={{ fontFamily: FONT, fontWeight: 600 }}>First-time customers only</Typography>}
                        />
                      )}
                    </Grid>
                  </Grid>
                </FormCard>

                <FormCard title="Validity" subtitle="When the coupon can be redeemed" accent="#10b981" icon={MdEventBusy}>
                  <Grid container spacing={2.5}>
                    <Grid item xs={12} md={4}>
                      {field('validFrom', 'Valid from', { type: 'datetime-local', required: true, shrink: true, view: fmtDateTime(formData.validFrom) })}
                    </Grid>
                    <Grid item xs={12} md={4}>
                      {field('validTill', 'Valid till', { type: 'datetime-local', required: true, shrink: true, view: fmtDateTime(formData.validTill) })}
                    </Grid>
                    <Grid item xs={12} md={4}>
                      {field('status', 'Status', {
                        select: true, required: true,
                        options: [{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }, { value: 'expired', label: 'Expired' }],
                        view: STATUS[formData.status]?.label
                      })}
                    </Grid>
                  </Grid>
                </FormCard>

                <FormCard title="Audience" subtitle="Who can use this coupon" accent="#ec4899" icon={MdPeople}>
                  <Grid container spacing={2.5}>
                    <Grid item xs={12} md={4}>
                      {field('applicableTo', 'Applicable to', {
                        select: true, required: true,
                        options: [{ value: 'all', label: 'Everyone' }, { value: 'b2b', label: 'B2B customers' }, { value: 'b2c', label: 'B2C customers' }],
                        view: AUDIENCE[formData.applicableTo]
                      })}
                    </Grid>
                    <Grid item xs={12} md={4}>
                      {formMode === 'view' ? viewField('Staff role', roleName ? roleName.charAt(0).toUpperCase() + roleName.slice(1) : '-') : (
                        <TextField select fullWidth size="small" label="Staff role (optional)" name="role_id" value={formData.role_id} onChange={handleInputChange}>
                          <MenuItem value="">None</MenuItem>
                          {roles.map((r) => (
                            <MenuItem key={r._id || r.id} value={r._id || r.id}>{(r.name || '').charAt(0).toUpperCase() + (r.name || '').slice(1)}</MenuItem>
                          ))}
                        </TextField>
                      )}
                    </Grid>
                    <Grid item xs={12} md={4}>
                      {formMode === 'view' ? viewField('Assigned to', personName(assignedUser) || currentCoupon?.assignToName || '-') : (
                        <TextField
                          select fullWidth size="small" label="Assign to (optional)" name="assignTo"
                          value={formData.assignTo} onChange={(e) => { handleInputChange(e); clearError('assignTo'); }}
                          disabled={!formData.role_id} error={!!errors.assignTo}
                          helperText={errors.assignTo || (!formData.role_id ? 'Pick a role first' : undefined)}
                        >
                          <MenuItem value="">None</MenuItem>
                          {filteredUsers.map((u) => <MenuItem key={u._id || u.id} value={u._id || u.id}>{personName(u)}</MenuItem>)}
                        </TextField>
                      )}
                    </Grid>
                  </Grid>
                </FormCard>

                <FormCard title="Card restrictions" subtitle="Optional – limit to certain payment cards" accent="#0ea5e9" icon={MdLocalOffer}>
                  <Grid container spacing={2.5}>
                    <Grid item xs={12} md={6}>{listField('applicableCardTypes', 'Card types', 'credit, debit', 'Separate with commas')}</Grid>
                    <Grid item xs={12} md={6}>{listField('allowedCardBins', 'Allowed card BINs', '431581, 123456', 'First 4–6 digits of the card, separated by commas')}</Grid>
                  </Grid>
                </FormCard>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                  <Button onClick={handleCloseForm} variant="outlined" sx={{ fontFamily: FONT, textTransform: 'none', fontWeight: 700, px: 3, py: 1, borderRadius: '10px', minWidth: 120, color: '#475569', borderColor: '#cbd5e1', '&:hover': { borderColor: '#94a3b8', backgroundColor: '#f1f5f9' } }}>
                    {formMode === 'edit' ? 'Cancel' : 'Close'}
                  </Button>
                  {formMode === 'edit' && (
                    <Button
                      type="submit" variant="contained" disabled={isSubmitting}
                      sx={{ fontFamily: FONT, textTransform: 'none', bgcolor: 'rgb(255 209 2)', color: 'black', px: 3, py: 1, borderRadius: '10px', minWidth: 180, fontWeight: 800, boxShadow: '0 4px 12px rgba(255, 209, 2, 0.3)', '&:hover': { bgcolor: 'rgb(255 190 0)' } }}
                    >
                      {isSubmitting ? <><CircularProgress size={18} sx={{ color: 'black', mr: 1 }} />{isEditing ? 'Updating…' : 'Creating…'}</> : isEditing ? 'Update coupon' : 'Create coupon'}
                    </Button>
                  )}
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} lg={4}>
              <Box sx={{ position: { lg: 'sticky' }, top: 16 }}>
                <CouponPreview form={formData} />
                {isEditing && currentCoupon && (
                  <Paper elevation={0} sx={{ mt: 2.5, p: 2.5, borderRadius: '14px', border: '1px solid #e2e8f0' }}>
                    <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 13.5, mb: 1 }}>Redemptions</Typography>
                    <Typography sx={{ fontFamily: FONT, fontSize: 25, fontWeight: 800 }}>
                      {currentCoupon.used}
                      {currentCoupon.usageLimit ? <Typography component="span" sx={{ fontSize: 13.5, color: '#64748b', fontWeight: 600 }}> / {currentCoupon.usageLimit}</Typography> : null}
                    </Typography>
                    {currentCoupon.usageLimit ? <LinearProgress variant="determinate" value={Math.min(100, (currentCoupon.used / currentCoupon.usageLimit) * 100)} sx={{ height: 8, borderRadius: 4, mt: 1 }} /> : <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#64748b' }}>No usage limit set</Typography>}
                  </Paper>
                )}
              </Box>
            </Grid>
          </Grid>
        </form>
      </>
    );
  };

  // ---------- render: list ----------
  const kpis = [
    { label: 'Total coupons', value: stats.total, icon: MdLocalOffer, gradient: 'linear-gradient(135deg,#6366f1,#8b5cf6)', key: '' },
    { label: 'Active', value: stats.active, icon: MdCheckCircle, gradient: STATUS.active.gradient, key: 'active' },
    { label: 'Inactive', value: stats.inactive, icon: MdPauseCircle, gradient: STATUS.inactive.gradient, key: 'inactive' },
    { label: 'Expired', value: stats.expired, icon: MdEventBusy, gradient: STATUS.expired.gradient, key: 'expired' }
  ];

  const renderCard = (c) => {
    const st = effectiveStatus(c);
    const cfg = STATUS[st] || STATUS.inactive;
    const pct = c.usageLimit ? Math.min(100, (c.used / c.usageLimit) * 100) : 0;
    const soldOut = c.usageLimit && c.used >= c.usageLimit;
    return (
      <Grid item xs={12} sm={6} xl={4} key={c.id}>
        <Paper elevation={0} sx={{ borderRadius: '16px', overflow: 'hidden', border: '1px solid #e2e8f0', display: 'flex', minHeight: 210, boxShadow: '0 4px 16px rgba(15,23,42,.05)', transition: 'transform .15s, box-shadow .15s', '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 12px 24px rgba(15,23,42,.12)' } }}>
          <Box onClick={() => handleOpenEditForm(c)} sx={{ cursor: 'pointer', width: '36%', background: st === 'active' ? 'linear-gradient(160deg,#0f172a,#334155)' : cfg.gradient, color: '#fff', p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
            <Typography sx={{ fontFamily: FONT, fontSize: 29, fontWeight: 800, lineHeight: 1.1 }}>{discountText(c)}</Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', opacity: 0.85 }}>OFF</Typography>
            {c.type === 'percentage' && c.maxDiscount ? <Typography sx={{ fontFamily: FONT, fontSize: 11.5, opacity: 0.85, mt: 0.75 }}>up to {inr(c.maxDiscount)}</Typography> : null}
          </Box>
          <Box sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
              <Box onClick={() => handleOpenEditForm(c)} sx={{ cursor: 'pointer', border: '2px dashed #cbd5e1', borderRadius: '8px', px: 1.25, py: 0.4, fontFamily: 'monospace', fontWeight: 800, fontSize: 12.5, letterSpacing: 1.5, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {c.code}
              </Box>
              <Pill status={st} />
            </Box>
            <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#475569' }}>
              Min. order <b>{inr(c.minOrderAmount)}</b>
            </Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#64748b' }}>
              {fmtDay(c.validFrom)} → {fmtDay(c.validTill)}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              <Chip size="small" label={AUDIENCE[c.applicableTo] || c.applicableTo} sx={{ fontFamily: FONT, fontWeight: 700, height: 22, fontSize: 11, background: '#e0e7ff', color: '#3730a3' }} />
              {c.isFirstTimeUserOnly && <Chip size="small" label="First order" sx={{ fontFamily: FONT, fontWeight: 700, height: 22, fontSize: 11, background: '#ede9fe', color: '#5b21b6' }} />}
              {c.assignToName && <Chip size="small" label={`For ${c.assignToName}`} sx={{ fontFamily: FONT, fontWeight: 700, height: 22, fontSize: 11, background: '#fce7f3', color: '#9d174d' }} />}
              {soldOut && <Chip size="small" icon={<MdWarningAmber />} label="Limit reached" sx={{ fontFamily: FONT, fontWeight: 700, height: 22, fontSize: 11, background: '#fef3c7', color: '#92400e', '& .MuiChip-icon': { color: 'inherit', fontSize: 12.5 } }} />}
            </Box>
            <Box sx={{ mt: 'auto' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography sx={{ fontFamily: FONT, fontSize: 11, color: '#64748b', fontWeight: 600 }}>Used</Typography>
                <Typography sx={{ fontFamily: FONT, fontSize: 11, fontWeight: 800, color: '#0f172a' }}>{c.used}{c.usageLimit ? ` / ${c.usageLimit}` : ' · no limit'}</Typography>
              </Box>
              <LinearProgress variant="determinate" value={c.usageLimit ? pct : 0} sx={{ height: 6, borderRadius: 3, background: '#f1f5f9', '& .MuiLinearProgress-bar': { background: pct >= 90 ? '#ef4444' : cfg.accent } }} />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.75 }}>
              <Tooltip title={copiedId === c.id ? 'Copied!' : 'Copy code'} arrow>
                <IconButton size="small" onClick={() => copyCode(c)} sx={{ color: '#475569', background: '#f1f5f9', borderRadius: '8px' }}><MdContentCopy size={16} /></IconButton>
              </Tooltip>
              <Tooltip title="View" arrow>
                <IconButton size="small" onClick={() => handleOpenEditForm(c)} sx={{ color: '#4f46e5', background: 'rgba(79,70,229,.1)', borderRadius: '8px' }}><MdVisibility size={16} /></IconButton>
              </Tooltip>
              <Tooltip title="Edit" arrow>
                <IconButton size="small" onClick={() => handleOpenEditForm(c, 'edit')} sx={{ color: '#ca8a04', background: '#fefce8', borderRadius: '8px' }}><MdEdit size={16} /></IconButton>
              </Tooltip>
              <Tooltip title="Delete" arrow>
                <IconButton size="small" onClick={() => setDeleteTarget(c)} sx={{ color: '#dc2626', background: 'rgba(220,38,38,.1)', borderRadius: '8px' }}><MdDeleteForever size={18} /></IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Paper>
      </Grid>
    );
  };

  const renderList = () => (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box>
          <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 25, color: '#0f172a' }}>Coupons</Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#64748b' }}>Create and track discount codes for your customers.</Typography>
        </Box>
        <Button
          variant="contained" startIcon={<MdAdd />} onClick={handleOpenAddForm}
          sx={{ fontFamily: FONT, background: 'rgb(255 209 2)', color: 'black', textTransform: 'none', borderRadius: '10px', fontWeight: 700, px: 3, boxShadow: '0 4px 12px rgba(255, 209, 2, 0.3)', border: '1px solid black', '&:hover': { background: 'rgb(255 209 2)', transform: 'translateY(-1px)' } }}
        >
          Add Coupon
        </Button>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 2, mb: 3 }}>
        {kpis.map((k) => (
          <Paper
            key={k.label} elevation={0} onClick={() => setFilter('status', k.key)}
            sx={{ p: 2.5, borderRadius: '16px', color: '#fff', background: k.gradient, display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer', outline: filters.status === k.key && (k.key || !filters.status) ? '3px solid #0f172a' : 'none', outlineOffset: 2, boxShadow: '0 4px 12px rgba(15,23,42,.1)', transition: 'transform .15s', '&:hover': { transform: 'translateY(-2px)' } }}
          >
            <Box sx={{ background: 'rgba(255,255,255,.22)', borderRadius: '14px', p: 1.5, display: 'flex' }}><k.icon size={26} /></Box>
            <Box>
              <Typography sx={{ fontSize: 11.5, fontWeight: 700, opacity: 0.92, fontFamily: FONT }}>{k.label}</Typography>
              <Typography sx={{ fontSize: 25, fontWeight: 800, lineHeight: 1.2, fontFamily: FONT }}>{k.value}</Typography>
            </Box>
          </Paper>
        ))}
      </Box>

      <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: '14px', border: '1px solid #e2e8f0', display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small" placeholder="Search by coupon code" value={codeInput} onChange={(e) => setCodeInput(e.target.value)}
          sx={{ flex: 1, minWidth: 220 }}
          InputProps={{ startAdornment: <MdSearch size={20} style={{ marginRight: 8, color: '#94a3b8' }} /> }}
        />
        <TextField select size="small" label="Type" value={filters.type} onChange={(e) => setFilter('type', e.target.value)} sx={{ minWidth: 130 }}>
          <MenuItem value="">All</MenuItem>
          <MenuItem value="percentage">Percentage</MenuItem>
          <MenuItem value="flat">Flat</MenuItem>
        </TextField>
        <TextField select size="small" label="Status" value={filters.status} onChange={(e) => setFilter('status', e.target.value)} sx={{ minWidth: 130 }}>
          <MenuItem value="">All</MenuItem>
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="inactive">Inactive</MenuItem>
          <MenuItem value="expired">Expired</MenuItem>
        </TextField>
        <TextField size="small" type="date" label="Starts on/after" InputLabelProps={{ shrink: true }} value={filters.validFrom} onChange={(e) => setFilter('validFrom', e.target.value)} />
        <TextField size="small" type="date" label="Ends on/before" InputLabelProps={{ shrink: true }} value={filters.validTill} onChange={(e) => setFilter('validTill', e.target.value)} />
        {hasFilters && <Button onClick={clearFilters} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Clear</Button>}
      </Paper>

      {loading ? (
        <Grid container spacing={2.5}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Grid item xs={12} sm={6} xl={4} key={i}><Skeleton variant="rounded" height={210} sx={{ borderRadius: '16px' }} /></Grid>
          ))}
        </Grid>
      ) : coupons.length === 0 ? (
        <Paper elevation={0} sx={{ py: 8, textAlign: 'center', borderRadius: '14px', border: '1px dashed #cbd5e1' }}>
          <Typography sx={{ fontFamily: FONT, fontWeight: 700, color: '#475569' }}>{hasFilters ? 'No coupons match your filters.' : 'No coupons yet. Create your first one.'}</Typography>
        </Paper>
      ) : (
        <Grid container spacing={2.5}>{coupons.map(renderCard)}</Grid>
      )}

      {meta.total > 0 && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Pagination
              count={meta.totalPages} page={meta.page} shape="rounded" disabled={loading} showFirstButton showLastButton
              onChange={(_, p) => setMeta((prev) => ({ ...prev, page: p }))}
              sx={{ '& .MuiPagination-ul': { flexWrap: 'nowrap' }, '& .MuiPaginationItem-root': { fontFamily: FONT, fontWeight: 600, borderRadius: '8px', '&.Mui-selected': { backgroundColor: 'rgb(255, 209, 2)', color: 'black', '&:hover': { backgroundColor: 'rgb(255, 190, 0)' } } } }}
            />
          </Box>
          <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
            Showing {(meta.page - 1) * meta.limit + 1} to {Math.min(meta.page * meta.limit, meta.total)} of {meta.total} coupons
          </Typography>
        </>
      )}
    </>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', p: { xs: 1.5, md: 3 } }}>
      <Box sx={{ maxWidth: 1600, mx: 'auto' }}>
        {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
        {showForm ? renderForm() : renderList()}
      </Box>

      <Dialog open={!!deleteTarget} onClose={() => !deleting && setDeleteTarget(null)} PaperProps={{ sx: { borderRadius: '14px', p: 1, minWidth: 380 } }}>
        <DialogTitle sx={{ fontFamily: FONT, fontWeight: 800 }}>Delete coupon?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: FONT }}>
            <b>{deleteTarget?.code}</b> will be removed and customers can no longer use it. This can't be undone from the panel.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Cancel</Button>
          <Button onClick={confirmDelete} disabled={deleting} variant="contained" sx={{ textTransform: 'none', fontWeight: 700, background: '#dc2626', '&:hover': { background: '#b91c1c' } }}>
            {deleting ? 'Deleting…' : 'Yes, delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CouponsManagement;
